/**
 * Main view component displaying AI Block execution history for an entity.
 */

import classNames from 'classnames'
import _ from 'lodash'
import React, { useState, useMemo } from 'react'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import 'browser/components/atomic-elements/organisms/ai-blocks/_ai-block-activity-view.scss'
import { Entity } from 'shared-libs/models/entity'
import { createFieldLabelGetter } from './ai-block-activity-helpers'
import type { IAiBlockActivityOptions } from './ai-block-activity-types'
import { AiBlockExecutionCard } from './ai-block-execution-card'
import { AiBlockActivityFilterBar } from './ai-block-activity-filter-bar'
import { AiBlockActivityGroup, type NumberedExecution } from './ai-block-activity-group'
import { applyFilters, FILTER_CONFIGS, type IActiveFilters } from './ai-block-activity-filters'

interface IAiBlockActivityViewProps extends IBaseProps {
  /** The core_blocks_usage mixin data from the entity */
  entity: Entity
  usageEntity: Entity | null
}

export const AiBlockActivityView: React.FC<IAiBlockActivityViewProps> = ({ className, entity, usageEntity }) => {
  const executions = usageEntity?.get('core_blocks_usage.executions') ?? []
  const [activeFilters, setActiveFilters] = useState<IActiveFilters>({})
  const [groupExpanded, setGroupExpanded] = useState(false)

  const options: IAiBlockActivityOptions = {
    getFieldLabel: createFieldLabelGetter(entity),
  }

  // Calculate total token usage across all executions
  const totalTokenUsage = useMemo(() => {
    let totalInput = 0
    let totalOutput = 0
    let totalTokens = 0

    executions.forEach((execution: any) => {
      if (execution.tokenUsage) {
        totalInput += execution.tokenUsage.input || 0
        totalOutput += execution.tokenUsage.output || 0
        totalTokens += execution.tokenUsage.total || 0
      }
    })

    return {
      input: totalInput,
      output: totalOutput,
      total: totalTokens,
    }
  }, [executions])

  // Apply filters and sort: most recent execution first
  const filteredExecutions = useMemo(() => {
    const filtered = applyFilters(executions, activeFilters, FILTER_CONFIGS)
    return _.orderBy(filtered, [(e) => e.creationDate], ['desc'])
  }, [executions, activeFilters])

  // Assign run numbers for display
  const numberedExecutions: NumberedExecution[] = useMemo(() => {
    return filteredExecutions.map((execution, idx) => ({
      execution,
      runNumber: filteredExecutions.length - idx,
    }))
  }, [filteredExecutions])

  if (_.isEmpty(executions)) {
    return (
      <div className={classNames('c-aiBlockResults', 'c-aiBlockResults--empty', className)}>
        <div className='c-aiBlockResults-emptyState'>
          <Icon
            icon={IconNames.CUBE}
            iconSize={32}
            className='c-aiBlockResults-emptyState-icon'
          />
          <p className='c-aiBlockResults-emptyState-message'>
            No AI Block results yet. Results will appear here after a block has been applied to this
            document.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={classNames('c-aiBlockResults', className)}>
      <AiBlockActivityFilterBar
        executions={executions}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        totalTokenUsage={totalTokenUsage}
      />

      {filteredExecutions.length === 0 ? (
        <div className='c-aiBlockResults-emptyState c-aiBlockResults-emptyState--filtered'>
          <Icon
            icon={IconNames.FILTER}
            iconSize={32}
            className='c-aiBlockResults-emptyState-icon'
          />
          <p className='c-aiBlockResults-emptyState-message'>
            No executions match the current filters. Try adjusting your filter selection.
          </p>
        </div>
      ) : numberedExecutions.length >= 2 ? (
        <AiBlockActivityGroup
          options={options}
          runs={numberedExecutions}
          expanded={groupExpanded}
          onToggle={() => setGroupExpanded(!groupExpanded)}
        />
      ) : (
        numberedExecutions.map(({ execution, runNumber }) => (
          <AiBlockExecutionCard
            key={execution.creationDate + runNumber}
            options={options}
            execution={execution}
            runNumber={runNumber}
          />
        ))
      )}
    </div>
  )
}
